import {
    AfterViewInit,
    Component,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import {
    CURRENT_STATE_PROJECT_KEY,
    getInsightsForNode,
    GraphData,
    Linqset,
    NODE_SUPERTYPE,
    ORGANISATION_MODEL_KEY,
    INSIGHTS_FIELD,
    MODEL_TYPE,
    WORK_TYPE,
} from '../../common/linqset-model';
import { createLinqset } from '../../common/model-processing';
import { OrganisationService } from '../../organisation/organisation.service';
import {
    createInsightsFilter,
    Currency,
    DEFAULT_CURRENCY,
    getCurrency,
    InsightsFilter,
    MODEL_FUNCTION,
    ModelDetails,
    Organisation,
} from '../../common/organisation';
import {
    AdjacencyTypes,
    GraphComponent,
    IEdge,
    IGraph,
    INode,
    IModelItem,
    FreeNodeLabelModel,
    LabelStyle,
} from '@yfiles/yfiles';
import { SCREEN } from '../layouts-enums';
import { AppComponent } from '../../app.component';
import {
    FUNCTION,
    FunctionService,
} from '../../model-services/function.service';
import { HelpService } from '../../help/help-services';
import { analysisTour } from '../../analysis/analysis-tour';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { InsightsService } from '../../model-services/insights.service';
import { AnalysisEdge, AnalysisNode, isUseOf } from '../../common/analysis-model';
import { analysisNodeTip, nodeTypeName, renderNodeTip, tipFormat } from '../../canvas/node-tooltip';
import { GraphSearchNode } from '../../ui-components/search/search.component';
import { findTagForOrganisation, getProjectName } from '../../common/organisation';
import {
    GroupItem,
    GroupSelectionComponent,
    ItemSelection,
} from '../../ui-components/group-selection/group-selection.component';
import { TagsService } from '../../organisation/tags/tags-service';
import {
    MODEL_API,
    ORGANISATION_API,
    RepositoryService,
} from '../../linq-utils/repository.service';
import { GraphService, HEATMAP_GRADIENT } from '../../canvas/graph.service';
import { channelsAt, createDimensionSummary, createHeatmapLegend, HeatmapLegend, readableTextOn } from './heatmap-legend';
import { DimensionSummary } from '../../epm/epm-scorecard-model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LABEL_TYPES } from '../../model-services/model-item-enums';
import { AnalysisService } from '../../model-services/analysis-service';
import { MatSidenav } from '@angular/material/sidenav';
import { FormattingService } from '../../linq-utils/formatting/formatting.service';
import { TemporalService } from '../../temporal/temporal.service';
import { MODEL_COLOURS, modelColours } from '../../lenses/lens';
import { AnalysisScope, filterForScope, modelsInScope, ScopeService } from '../../analysis/scope';
import { defaultGraphLevel, GRAPH_LEVELS, GraphLevel, graphAtLevel, GroupedNode, isGrouped, isOtherModel, joinsBetween, withOtherModels } from '../../analysis/graph-levels';

import { UserService } from '../../user/user.service';
import { BusinessProcessLayoutOrientation } from '../../business-process/services/business-process-layout.service';
import { FullModelComponent } from '../../analysis/full-model.component';
import { buildSimulationReport, RoleCapacity, SimulationReport, Suggestion } from '../../temporal/simulation-report';
import { roleCapacitiesFrom } from '../../temporal/scenario-comparison';
import { ScenarioDialogComponent, ScenarioDialogData, ScenarioDialogResult } from '../../temporal/scenario-dialog.component';
import { WhatIfDialogComponent, WhatIfDialogData } from '../../temporal/whatif-dialog.component';
import { RecommendationsDialogComponent, RecommendationsDialogData, RecommendationsDialogResult } from '../../temporal/recommendations-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { USER_ROLE } from '../../common/user';
import { BusinessProcessDefinition } from '../../business-process/models/business-process.model';

export enum EPM_DIMENSION {
    // what each step supports (the value of everything that depends on it) and what it contributes (its share, so the
    // steps add up to the whole): see value-contribution.ts
    REVENUE = 'Revenue supported',
    REVENUE_CONTRIBUTION = 'Revenue contribution',
    // how much more a step's work costs than the revenue it creates: the steps that only add cost. The heatmap's scale
    // runs from nothing up, so it shows the shortfall rather than the margin, which can be either side of nothing
    COST_ABOVE_REVENUE = 'Cost above revenue',
    // the revenue lost if a step stopped, as its items have no other route: steps on one route each risk the same
    // revenue, so it doesn't add up
    REVENUE_AT_RISK = 'Revenue at risk',
    COST = 'Cost',
    FRICTION = 'Friction',
    CARBON = 'Carbon',
    VALUE = 'Value supported',
    VALUE_CONTRIBUTION = 'Value contribution',
    DERIVED_VALUE = 'Derived value',
    NO_VALUE = 'No value',
    OUTPUTS_SUPPORTED = 'Number of outputs supported',
    TIME = 'Time',
    RISK = 'Risk',
    OPTIONS = 'Options',
    UTILISATION = 'Utilisation',
    THROUGHPUT = 'Throughput (items per hour)',
    CENTRALITY = 'Centrality',
}

// dimensions whose step values add up to a meaningful total; the rest are rates, ratios or rankings
// value and revenue supported aren't: every step on the way to an output supports all of it, so their contributions are
const ADDITIVE_DIMENSIONS = [EPM_DIMENSION.REVENUE_CONTRIBUTION, EPM_DIMENSION.COST_ABOVE_REVENUE, EPM_DIMENSION.COST, EPM_DIMENSION.FRICTION, EPM_DIMENSION.CARBON, EPM_DIMENSION.VALUE_CONTRIBUTION, EPM_DIMENSION.TIME];

// a step marked as not adding value: waste other than exception work, so friction
const isWaste = (node: AnalysisNode) => node.superType === NODE_SUPERTYPE.ACTION && !node.exceptionWork && node.workKind === WORK_TYPE.NOT_VALUE_ADDING;

// a step's cost beyond the revenue it creates; nothing for information, whose cost is the work producing it
const costAboveRevenue = (node: AnalysisNode) =>
    node.superType === NODE_SUPERTYPE.ACTION ? Math.max(0, (node.cost || 0) - (node.contributedRevenue || 0)) : 0;

// dimensions the analysis data has no values for yet, so they aren't offered
const UNMEASURED_DIMENSIONS = [EPM_DIMENSION.OPTIONS, EPM_DIMENSION.RISK, EPM_DIMENSION.THROUGHPUT];

// dimensions a node standing for a run of steps can show: the sum of its steps'. Revenue and
// value flow through each step, so a sum would count them again at every one, and rates and rankings don't add up
const GROUPED_DIMENSIONS = [EPM_DIMENSION.TIME, EPM_DIMENSION.COST, EPM_DIMENSION.CARBON, EPM_DIMENSION.FRICTION, EPM_DIMENSION.VALUE_CONTRIBUTION, EPM_DIMENSION.REVENUE_CONTRIBUTION, EPM_DIMENSION.COST_ABOVE_REVENUE];
// a model not shown that has no colour of its own
const OTHER_MODEL_COLOUR = '#64748b';

// once the browser has had the chance to draw what has changed, such as a spinner, before work that holds the page up.
// Frames pause while the page is hidden, so it carries on after a moment anyway
const afterPaint = () => new Promise<void>((resolve) => {
    let done = false;
    const go = () => { if (!done) { done = true; setTimeout(resolve); } };
    requestAnimationFrame(go);
    setTimeout(go, 50);
});

@Component({
    selector: 'app-analysis-layout',
    templateUrl: './analysis-layout.html',
    styleUrls: ['./analysis-layout.css'],
    standalone: false,
})
export class AnalysisLayoutComponent implements AfterViewInit, OnDestroy {
    organisation: Organisation;
    organisationKey: string;
    routeSub: Subscription;
    workspaceKey: string = CURRENT_STATE_PROJECT_KEY;

    // the workspace's name, for the scorecard to say what it shows
    get workspaceName(): string {
        return (this.organisation && getProjectName(this.organisation, this.workspaceKey)) || 'Current state';
    }

    loading: boolean = true;
    graphComponent!: GraphComponent;
    screen: SCREEN = SCREEN.ANALYSIS;
    linqset: Linqset; // dummy linqset to make functions work

    organisationModelData: GraphData;
    filter: InsightsFilter;
    organisationData: { nodes: AnalysisNode[]; edges: AnalysisEdge[] } = {
        nodes: [],
        edges: [],
    };

    modelsData: GroupItem[];
    models: ModelDetails[] = [];
    modelsList: { _key: string; colour: string; name: string }[] = [];
    // the models drawn in the graph: those shown, and those they join or use, each drawn as one node
    graphModels: { _key: string; colour: string; name: string }[] = [];

    selectedDimension: EPM_DIMENSION = undefined;
    dimensions: EPM_DIMENSION[] = [];
    heatmap: boolean = false;
    heatmapLegend?: HeatmapLegend;
    dimensionSummary?: DimensionSummary;
    currency: Currency;

    processDefinition: BusinessProcessDefinition;

    // how much detail the graph shows: a node for each model, each model's main flow, or every action. Large
    // organisations open at model level; once the user picks a level it's kept as they change the models shown
    level: GraphLevel = 'actions';
    private levelChosen = false;
    readonly levels = GRAPH_LEVELS;
    // every action of the models shown, which the scorecard scores whatever level the graph is at
    shownData: { nodes: AnalysisNode[]; edges: AnalysisEdge[] } = { nodes: [], edges: [] };

    refreshTrigger: any = {};
    private centralityFound = false;

    // value badges, one style for each shade of the heatmap so thousands of steps share a few
    private badgeStyles = new Map<number, LabelStyle>();

    @ViewChild('rightdrawer') public rightDrawer: MatSidenav;

    constructor(
        private organisationService: OrganisationService,
        private functionService: FunctionService,
        private route: ActivatedRoute,
        private insightsService: InsightsService,
        private tagService: TagsService,
        private repoService: RepositoryService,
        private graphService: GraphService,
        private snackbar: MatSnackBar,
        private dialog: MatDialog,
        private analysisService: AnalysisService,
        private formatter: FormattingService,
        private temporalService: TemporalService,
        private scopeService: ScopeService,
        private userService: UserService,
        private helpService: HelpService,
    ) {}

    // the graph lays out in the direction the user configured for the model canvas
    get layoutOrientation(): BusinessProcessLayoutOrientation {
        return (this.userService.getUserPreferences()?.graphDirection as BusinessProcessLayoutOrientation) || 'top-to-bottom';
    }

    private scope?: AnalysisScope;
    private scopeSub?: Subscription;
    fullModel = false;                  // the LINQ model itself is shown in place of the process graph
    fullModelField?: INSIGHTS_FIELD;    // the attribute the full model is coloured by
    readonly insightFields = Object.values(INSIGHTS_FIELD).filter((f) => f !== INSIGHTS_FIELD.NONE);
    @ViewChild(FullModelComponent) private fullModelView?: FullModelComponent;

    get currentScope() {
        return this.scope;
    }

    // the view switched between the process graph and the full model. The process graph's heatmap goes with it (it is
    // drawn again, without one, on the way back); the full model keeps its own colouring
    fullModelChanged(on: boolean) {
        if (on === this.fullModel) return;
        if (on) {
            this.dimensionSelected(undefined);
            this.temporalService.clear();
        } else if (this.level !== 'models') {
            // the step graph is built again for the models now shown, and reports back when it's done; the model
            // diagram draws without reporting back
            this.loading = true;
        }
        this.fullModel = on;
    }

    fullModelFieldChanged(field?: INSIGHTS_FIELD) {
        this.fullModelField = field;
        void this.fullModelView?.showHeatmap(field);
    }
    private focusKey?: string;          // a step to show once the graph is built, from a link

    ngAfterViewInit(): void {
        if (!this.routeSub) {
            setTimeout(() => {
                this.routeSub = this.route.params.subscribe(async (p) => {
                    this.organisationKey = p['organisation_key'];
                    this.focusKey = this.route.snapshot.queryParams['focus'];
                    this.scope = this.scopeService.open(this.organisationKey);
                    this.workspaceKey = this.scope.workspaceKey;
                    // neither needs the other, so they load together
                    [this.organisationModelData, this.organisation] =
                        await Promise.all([
                            this.insightsService.afterRefresh(this.workspaceKey, () => this.repoService.callSingleAPI(
                                this.repoService.getData<GraphData>(
                                    MODEL_API(
                                        this.organisationKey,
                                        ORGANISATION_MODEL_KEY,
                                        this.workspaceKey,
                                    ),
                                ),
                                'AnalysisLayoutComponent',
                                'loadOrganisationModelData',
                            )),
                            this.organisationService.checkOrganisationDataForRoute(
                                this.organisationKey,
                            ),
                        ]);

                    this.currency = getCurrency(
                        this.organisation?.currencyCode || DEFAULT_CURRENCY,
                    );
                    this.dimensions = Object.values(EPM_DIMENSION).filter(
                        (d) => !UNMEASURED_DIMENSIONS.includes(d),
                    );

                    this.linqset = createLinqset(
                        this.organisation,
                        createInsightsFilter(),
                    );

                    this.setModelsData();
                    this.loadOrganisationData();
                });
            });
            // the scope changed on the header: new models are a new graph; a new workspace is new data
            this.scopeSub = this.scopeService.scope$.subscribe((scope) => {
                const previous = this.scope;
                this.scope = scope;
                if (!previous || !this.organisation) return;
                if (scope.workspaceKey !== previous.workspaceKey) {
                    this.workspaceKey = scope.workspaceKey;
                    void this.reloadWorkspace();
                } else {
                    this.filter = filterForScope(scope);
                    this.loadGraph();
                }
            });
        }
    }

    // the organisation model and the analysis for another workspace
    private async reloadWorkspace() {
        this.loading = true;
        try {
            this.organisationModelData = await this.insightsService.afterRefresh(this.workspaceKey, () => this.repoService.callSingleAPI(
                this.repoService.getData<GraphData>(MODEL_API(this.organisationKey, ORGANISATION_MODEL_KEY, this.workspaceKey)),
                'AnalysisLayoutComponent',
                'loadOrganisationModelData',
            ));
            this.setModelsData();
            await this.loadOrganisationData();
        } catch (err) {
            console.error(err);
            this.loading = false;
        }
    }

    ngOnDestroy(): void {
        this.helpService.endTour();
        if (this.routeSub) this.routeSub.unsubscribe();
        this.scopeSub?.unsubscribe();
        window.clearInterval(this.reportTimer);
        this.temporalService.clear();
        // the graph belongs to the business process graph component, which cleans it up itself
    }

    async loadOrganisationData() {
        this.loading = true;
        try {
            this.filter = filterForScope(this.scope || this.scopeService.scope);
            this.roleCapacities = undefined;
            this.organisationData =
                await this.insightsService.loadAnalysisGraphForFilter(
                    this.organisation,
                    this.filter,
                );
            // the graph is drawn once the models shown are known, at the level of detail for them (see showLevel)
        } finally {
            this.loading = false;
        }
        // start with every model shown rather than an empty graph
        this.loadGraph();
    }

    async loadOrganisationGraph() {
        this.scopeService.widen();
    }

    // the business process graph rebuilds itself when its models change, then reports back through graphBuilt
    loadGraph() {
        // the full model shows in its place, so the graph isn't built and won't report back to end the spinner
        this.loading = !this.fullModel;
        this.setModelsList();
    }

    onGraphBuilt(graphComponent: GraphComponent) {
        // a simulation of the old graph can't carry on in the new one
        this.temporalService.clear();
        this.refreshSimulationReport();
        this.graphComponent = graphComponent;
        // centrality takes seconds for large organisations and only the centrality dimension shows it, so it's
        // worked out when that is chosen
        this.centralityFound = false;
        if (this.focusKey) {
            const key = this.focusKey;
            this.focusKey = undefined;
            void this.showStep(key);
        } else if (this.focusModelKey) {
            const key = this.focusModelKey;
            this.focusModelKey = undefined;
            this.selectModel(key);
        }
        try {
            this.analysisService.friction(graphComponent.graph);
        } catch (err) {
            console.error('Unable to analyse the process graph', err);
        } finally {
            this.heatmap = false;
            this.refreshTrigger = {};
            this.loading = false;
        }
        // the rebuilt graph has none of the chosen dimension's heatmap or values, so show them again
        if (this.selectedDimension) this.dimensionSelected(this.selectedDimension);
    }

    // items run through each step, so a simulation needs every action drawn
    get canSimulate() {
        return this.level === 'actions';
    }

    get simulating() {
        return this.temporalService.isRunning;
    }

    // a simulation that has been stopped or has finished its year, whose queues are still showing
    get simulationStopped() {
        return !this.temporalService.isRunning && this.temporalService.hasSimulation;
    }

    get simulationFinished() {
        return this.temporalService.isFinished;
    }

    get simulationProgress() {
        return this.temporalService.progress;
    }

    // runs items through the graph for a year from the start, showing where they queue
    async startSimulation() {
        if (!this.graphComponent) return;
        if (!this.canSimulate) {
            this.snackbar.open('Show every action to run a simulation: it runs items through each step', 'close', { duration: 4000 });
            return;
        }
        // the simulation draws its own heatmap of queues, so the chosen dimension's makes way for it
        if (this.selectedDimension) this.dimensionSelected(undefined);
        this.showFullResults = false;
        await this.temporalService.runAnimation(this.graphComponent, 30);
        this.watchSimulation();
    }

    // stops where it is, so the queues can be looked at, then carried on, started again or cleared
    stopSimulation() {
        this.temporalService.stopAnimation();
        this.refreshSimulationReport();
    }

    continueSimulation() {
        this.showFullResults = false;
        this.temporalService.continueAnimation();
        this.watchSimulation();
    }

    clearSimulation() {
        this.temporalService.clear();
        this.refreshSimulationReport();
    }

    // results of the simulation shown over the graph: a summary while it runs, the full results once it stops
    simulationReport?: SimulationReport;
    showFullResults = false;
    private reportTimer?: number;
    private roleCapacities?: Map<string, RoleCapacity>;

    // the results are worked out every second while the simulation runs, as working them out every tick isn't needed
    private watchSimulation() {
        window.clearInterval(this.reportTimer);
        this.refreshSimulationReport();
        this.reportTimer = window.setInterval(() => this.refreshSimulationReport(), 1000);
    }

    private refreshSimulationReport() {
        const snapshot = this.temporalService.snapshot();
        if (!snapshot) {
            window.clearInterval(this.reportTimer);
            this.simulationReport = undefined;
            this.showFullResults = false;
            return;
        }
        this.simulationReport = buildSimulationReport(snapshot, this.getRoleCapacities(), new Map((this.organisation?.models || []).map((m) => [m._key, m.name])));
        // stopped or finished its year: the full results take over from the summary
        if (!this.temporalService.isRunning) {
            window.clearInterval(this.reportTimer);
            this.reportTimer = undefined;
            this.showFullResults = true;
        }
    }

    // each role's work and capacity for the workspace, from the organisation model
    private getRoleCapacities() {
        if (!this.roleCapacities) this.roleCapacities = roleCapacitiesFrom(this.organisationModelData, this.workspaceKey);
        return this.roleCapacities;
    }

    // suggestions are tried in a project made from the current state, by those who can add projects
    get canTestSuggestions() {
        return this.workspaceKey === CURRENT_STATE_PROJECT_KEY && !!this.organisation && this.organisation.role !== USER_ROLE.READ;
    }

    get testDisabledReason() {
        return this.workspaceKey !== CURRENT_STATE_PROJECT_KEY ? 'Suggestions are tested against the current state' : "You don't have access to add projects";
    }

    // recommendations from AI, from the EPM scorecard and the simulation; those chosen are tried in a project
    openRecommendations() {
        const snapshot = this.temporalService.snapshot();
        if (!this.organisation || !this.simulationReport || !snapshot || !this.organisationData || !this.organisationModelData) return;
        const data: RecommendationsDialogData = {
            source: {
                organisation: this.organisation,
                modelKeys: this.modelsList.map((m) => m._key),
                analysis: this.organisationData,
                organisationModel: this.organisationModelData,
                report: this.simulationReport,
                snapshot,
                currency: this.currency.code,
                money: (value) => this.formatter.formatAbbreviatedNumber(value, this.currency),
                number: (value) => this.formatter.formatAbbreviatedNumber(value),
            },
            canTest: this.canTestSuggestions,
            testDisabledReason: this.testDisabledReason,
        };
        this.dialog
            .open<RecommendationsDialogComponent, RecommendationsDialogData, RecommendationsDialogResult>(RecommendationsDialogComponent, { data, width: '820px', maxWidth: '95vw', autoFocus: false })
            .afterClosed()
            .subscribe((result) => {
                if (result?.test?.length) this.testSuggestions(result.test, true);
            });
    }

    // what if the load or the people on a role changed: the roles' needs and a simulation against today, in the browser
    openWhatIf() {
        if (!this.organisation || !this.organisationData || !this.organisationModelData) return;
        const data: WhatIfDialogData = {
            organisation: this.organisation,
            analysis: this.organisationData,
            modelKeys: this.modelsList.map((m) => m._key),
            organisationModel: this.organisationModelData,
            workspaceKey: this.workspaceKey,
            formatCost: (value) => this.formatter.formatAbbreviatedNumber(value, this.currency),
        };
        this.dialog.open<WhatIfDialogComponent, WhatIfDialogData>(WhatIfDialogComponent, { data, width: '860px', maxWidth: '95vw', autoFocus: false });
    }

    // tries suggestions in a new project and compares it with the current state; the user keeps or deletes it
    testSuggestions(suggestions: Suggestion[], chooseAll = false) {
        if (!this.organisation || !suggestions.length) return;
        const data: ScenarioDialogData = {
            organisation: this.organisation,
            suggestions,
            modelKeys: this.modelsList.map((m) => m._key),
            formatCost: (value) => this.formatter.formatAbbreviatedNumber(value, this.currency),
            chooseAll,
        };
        this.dialog
            .open<ScenarioDialogComponent, ScenarioDialogData, ScenarioDialogResult>(ScenarioDialogComponent, { data, width: '760px', maxWidth: '95vw', autoFocus: false })
            .afterClosed()
            .subscribe((result) => {
                if (result?.keptProjectKey) this.snackbar.open('The project was kept', 'close', { duration: 4000 });
            });
    }

    // a step chosen in the simulation's results
    async showSimulationStep(key: string) {
        this.showFullResults = false;
        await this.showStep(key);
    }

    // the simulation button on the toolbar starts a simulation, or stops the one running
    toggleSimulation() {
        if (this.temporalService.isRunning) this.stopSimulation();
        else this.startSimulation();
    }

    // a step chosen on the scorecard: the scorecard closes, as it covers most of the graph, and the step is selected and
    // zoomed to
    async showStep(key: string) {
        // at model level the steps aren't drawn: show them, a run of steps at a time, and then this one
        if (this.level === 'models') {
            this.focusKey = key;
            void this.setLevel('flow');
            return;
        }
        const node = this.graphNodeFor(key);
        if (!node) return;
        await this.rightDrawer.close();
        this.graphComponent.selection.clear();
        this.graphComponent.selection.add(node);
        await this.graphComponent.zoomToAnimated(1, node.layout.center);
    }

    // a node found with search: selected and zoomed to, in the process graph or the full model
    findNode(key: string) {
        const name = this.searchNodes.find((n) => n.key === key)?.name || 'That node';
        if (this.fullModel) {
            if (!this.fullModelView?.trace(key)) this.snackbar.open(`${name} isn't in the model`, 'close', { duration: 4000 });
            return;
        }
        if (this.level === 'models') {
            void this.showStep(key);
            return;
        }
        const node = this.graphNodeFor(key);
        if (!node) {
            this.snackbar.open(`${name} isn't in the graph`, 'close', { duration: 4000 });
            return;
        }
        this.graphComponent.selection.clear();
        this.graphComponent.selection.add(node);
        void this.graphComponent.zoomToAnimated(Math.max(this.graphComponent.zoom, 1), node.layout.center);
    }

    public async handleFunctionEvent(event: {
        function: FUNCTION;
        value?: any;
    }) {
        if (event.function === FUNCTION.TOGGLE_INSIGHTS) {
            if (this.rightDrawer.opened) {
                await this.rightDrawer.close();
            } else {
                await this.rightDrawer.open();
            }
            return;
        }
        if (event.function === FUNCTION.TOGGLE_TEMPORAL_MODEL) {
            this.toggleSimulation();
            return;
        }
        if (event.function === FUNCTION.SCREEN_TOUR) {
            this.helpService.showWhatsThisOverlay(analysisTour('actions'));
            return;
        }
        try {
            await this.functionService.executeFunction(
                event.function,
                event.value,
                undefined,
                undefined,
                this.graphComponent,
            );
        } catch (err) {
            console.error(err);
        }
    }

    // the steps and information in the models shown, for search to find
    searchNodes: GraphSearchNode[] = [];

    setModelsList() {
        // the workspace's models: a project's own, made in it, as well as the current state's
        this.models = modelsInScope(this.scope || this.scopeService.scope, this.organisation);
        const shown = new Set(this.models.map((m) => m._key));
        const names = new Map(this.models.map((m) => [m._key, m.name]));
        this.searchNodes = this.organisationData.nodes
            .filter((n) => n.name && shown.has(n.modelKey))
            .map((n) => ({ key: n._key, name: n.name, description: `${nodeTypeName(n.type, n.superType)} · ${names.get(n.modelKey) || ''}`, type: n.type }));
        // a model keeps its colour whichever models are shown, and in every lens
        const colours = modelColours(this.organisation);
        this.modelsList = this.models.map((model, index) => {
            return {
                _key: model._key,
                name: model.name,
                colour: colours.get(model._key) || MODEL_COLOURS[index % MODEL_COLOURS.length],
            };
        });
        if (!this.levelChosen) this.level = defaultGraphLevel(this.organisationData, this.modelsList.map((m) => m._key));
        this.showLevel();
    }

    // the graph of the models shown, at the level of detail chosen
    private showLevel() {
        this.shownData = graphAtLevel(this.organisationData, 'actions', this.modelsList);
        if (this.level === 'models') {
            void this.showModels();
            return;
        }
        const level = this.level === 'actions' ? this.shownData : graphAtLevel(this.organisationData, this.level, this.modelsList);
        // the models not shown that these join or use, or that join or use them, each as a node of its own
        const names = new Map((this.organisation?.models || []).map((m) => [m._key, m.name]));
        const shown = withOtherModels(this.organisationData, level, this.modelsList, (key) => names.get(key));
        const colours = modelColours(this.organisation);
        this.graphModels = [...this.modelsList, ...shown.nodes.filter(isOtherModel)
            .map((n) => ({ _key: n.modelKey, name: n.name, colour: colours.get(n.modelKey) || OTHER_MODEL_COLOUR }))];
        this.processDefinition = { nodes: shown.nodes, connections: shown.edges };
    }

    // the models shown and the joins between them, drawn as the home screen's model diagram is
    readonly MODEL_TYPE = MODEL_TYPE;
    modelsLinqset?: Linqset;
    private joins?: { workspaceKey: string; data: GraphData };
    private async showModels() {
        this.loading = true;
        // the step graph isn't shown, so there's nothing in it to find or colour
        this.graphComponent = undefined;
        try {
            if (this.joins?.workspaceKey !== this.workspaceKey) {
                const data = await this.repoService.callSingleAPI(
                    this.repoService.getData<GraphData>(ORGANISATION_API(this.organisationKey, this.workspaceKey) + '/joins'),
                    'AnalysisLayoutComponent', 'loadJoins');
                this.joins = { workspaceKey: this.workspaceKey, data: data || { nodes: [], edges: [] } };
            }
            this.modelsLinqset = createLinqset(this.organisation, this.filter, joinsBetween(this.joins.data, this.modelsList.map((m) => m._key)));
            // the diagram is drawn on the next check, with the spinner still showing
            await afterPaint();
        } catch (err) {
            console.error('Unable to load the joins between models', err);
            this.snackbar.open("The models' joins couldn't be loaded", 'close', { duration: 4000 });
        } finally {
            this.loading = false;
        }
    }

    // a model double-clicked in the model diagram: its steps, a run of steps at a time
    private focusModelKey?: string;
    openModelSteps(item: IModelItem) {
        const key = item instanceof INode ? item.tag?.model?._key : undefined;
        if (!key) return;
        this.focusModelKey = key;
        void this.setLevel('flow');
    }

    // a model not shown, double-clicked where it joins the graph: the views look at it instead
    openOtherModel(item: INode) {
        const node: AnalysisNode | undefined = item.tag?.node;
        if (!isOtherModel(node)) return;
        const model = this.organisation?.models?.find((m) => m._key === node.modelKey);
        if (!model || model.role === undefined || model.role === null || model.role < 0) {
            this.snackbar.open(`You don't have access to ${node.name}`, 'close', { duration: 4000 });
            return;
        }
        this.scopeService.narrowTo([model._key]);
    }

    // the user picks how much detail the graph shows: it's drawn again, without a heatmap or simulation the level can't show
    async setLevel(level: GraphLevel) {
        if (level === this.level || this.pendingLevel) return;
        this.levelChosen = true;
        // building a graph holds the page up, so the spinner is drawn before it starts
        this.pendingLevel = level;
        this.loading = true;
        await afterPaint();
        this.pendingLevel = undefined;
        this.level = level;
        if (this.selectedDimension && !this.availableDimensions.includes(this.selectedDimension)) this.dimensionSelected(undefined);
        if (level !== 'actions') {
            this.temporalService.clear();
            this.refreshSimulationReport();
        }
        this.showLevel();
    }
    // the level being changed to, which the control shows while the graph is built
    pendingLevel?: GraphLevel;

    // what the level shows, beside the control
    get levelNote(): string {
        switch (this.level) {
            case 'models': return 'A node for each model, joined where one joins or uses information from another, as on the home screen. Double-click a model to see its steps.';
            case 'flow': return "Each model's main flow: the route most items take, step by step. The work off it is gathered into one node where it leaves or joins the main flow. Heatmaps add up those nodes' steps.";
            default: return 'Every action. Run a simulation, or use any heatmap.';
        }
    }

    // the graph's subtitle at its level
    get levelSubtitle(): string {
        switch (this.level) {
            case 'models': return 'How the models join to one another';
            case 'flow': return "Each model's main flow, with the work off it gathered up";
            default: return 'How work flows from step to step';
        }
    }

    // the dimensions the graph can show at its level
    get availableDimensions(): EPM_DIMENSION[] {
        if (this.level === 'models') return [];
        return this.level === 'actions' ? this.dimensions : this.dimensions.filter((d) => GROUPED_DIMENSIONS.includes(d));
    }

    // the node drawn for a step: the step itself, or at a higher level the model or run of steps it is in
    private graphNodeFor(key: string): INode | undefined {
        const nodes = this.graphComponent?.graph.nodes;
        return nodes?.find((n) => n.tag?.node?._key === key) || nodes?.find((n) => isGrouped(n.tag?.node) && n.tag.node.members.some((m: AnalysisNode) => m._key === key));
    }

    selectModel(key: string) {
        if (!this.graphComponent) return;
        const nodes = this.graphComponent.graph.nodes
            .filter((n) => n.tag?.node?.modelKey === key)
            .toArray();
        if (nodes.length === 0) {
            this.snackbar.open("That model has nothing in the graph to zoom to", 'close', { duration: 4000 });
            return;
        }
        this.graphService.zoomToSetOfModelNodes(this.graphComponent, nodes);
    }

    isHelpOn() {
        return AppComponent.help;
    }

    setModelsData() {
        const functions: MODEL_FUNCTION[] = [];
        this.modelsData = [];
        const projectChangedGroup = new GroupItem(
            'changed',
            undefined,
            'Changed models',
            undefined,
            false,
            undefined,
            [],
        );
        const modelsGroup = new GroupItem(
            'models',
            undefined,
            'Models',
            undefined,
            false,
            undefined,
            [],
        );

        this.organisation.models
            .filter(
                (m) =>
                    (m.projectKey === this.workspaceKey ||
                        m.projectKey === CURRENT_STATE_PROJECT_KEY) &&
                    !m.deletedDate &&
                    m.role,
            )
            .sort((a, b) =>
                a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
            )
            .forEach((m) => {
                if (functions.indexOf(m.function) < 0)
                    functions.push(m.function);
                const item = new GroupItem(m._key, undefined, m.name);
                if (
                    this.workspaceKey !== CURRENT_STATE_PROJECT_KEY &&
                    m.projects.indexOf(this.workspaceKey) >= 0
                ) {
                    item.parentKey = projectChangedGroup._key;
                    projectChangedGroup.items.push(item);
                } else {
                    item.parentKey = modelsGroup._key;
                    modelsGroup.items.push(item);
                }
            });

        // clear this so only used first time
        if (this.workspaceKey !== CURRENT_STATE_PROJECT_KEY)
            this.modelsData.push(projectChangedGroup);
        this.modelsData.push(modelsGroup);
        this.modelsData.push(
            new GroupItem(
                'functions',
                undefined,
                'Model functions',
                undefined,
                false,
                undefined,
                functions.map((f) => {
                    return new GroupItem(f, 'functions', f);
                }),
            ),
        );
        this.modelsData.push(
            new GroupItem(
                'tags',
                undefined,
                'Model tags',
                undefined,
                false,
                undefined,
                (this.organisation.modelTags?.tags || []).map((t) => {
                    return new GroupItem(
                        t._key,
                        'tags',
                        t.name,
                        this.tagService.getIconForTag(t),
                    );
                }),
            ),
        );
    }

    clearFiler() {
        this.filter.modelFunctions = [];
        this.filter.modelKeys = [];
        this.filter.modelTags = [];
    }

    dimensionSelected(dimension?: EPM_DIMENSION) {
        this.selectedDimension = dimension;
        if (!this.graphComponent) return;
        if (this.heatmap) {
            this.graphService.removeHeatmap(this.graphComponent);
            this.heatmap = false;
        }

        // and the other way round: a dimension's heatmap replaces the simulation's, running or finished
        if (dimension) {
            this.temporalService.clear();
            this.refreshSimulationReport();
        }

        if (!dimension) {
            // the heatmap is gone, so its values, key and the message announcing it shouldn't stay either
            this.heatmapLegend = undefined;
            this.dimensionSummary = undefined;
            this.removeDimensionLabels();
            this.snackbar.dismiss();
        } else {
            if (dimension === EPM_DIMENSION.CENTRALITY && !this.centralityFound) {
                this.analysisService.centrality(this.graphComponent.graph);
                this.centralityFound = true;
            }
            const max = this.setValuesForHeatmap(dimension);
            // steps and connections glow on one scale, so a step compares directly with the routes leaving it, and the
            // legend reads for both
            const scale = Math.max(max.maxNode, max.maxEdge);
            this.labelSteps(dimension, scale);
            this.heatmapLegend = createHeatmapLegend(
                HEATMAP_GRADIENT,
                scale,
                this.getEPMValueFormatter(dimension),
                dimension,
            );
            this.dimensionSummary = this.summariseDimension(dimension, scale);
            const r = this.graphService.addHeatmap(
                this.graphComponent,
                scale,
                scale,
                false,
                true,
            );
            if (r)
                this.snackbar.open('Showing heatmap for ' + dimension, 'close');

            this.heatmap = true;
        }
    }

    setValuesForHeatmap(dimension: EPM_DIMENSION): {
        maxNode: number;
        maxEdge: number;
    } {
        const { graph } = this.graphComponent;

        const fnNode = this.getEPMFunctionForNode(dimension);
        let maxNode = 0;
        let maxEdge = 0;
        graph.nodes.forEach((n) => {
            const analysisNode = n.tag.node;
            n.tag.value = fnNode(analysisNode);
            if (n.tag.value > maxNode) maxNode = n.tag.value;

            graph.edgesAt(n, AdjacencyTypes.OUTGOING).forEach((e) => {
                // not rounded: fractional dimensions such as centrality would all become 0
                e.tag.value = n.tag.value * e.tag.edge.proportion;
                if (e.tag.value > maxEdge) maxEdge = e.tag.value;
            });
        });
        return { maxNode, maxEdge };
    }

    // what the scorecard shows of the chosen dimension: its total where that means something, and its highest steps
    private summariseDimension(dimension: EPM_DIMENSION, scale: number): DimensionSummary {
        const label = this.getEPMLabelForNode(dimension);
        const steps = this.graphComponent.graph.nodes
            .filter((n) => !!n.tag?.node && !isOtherModel(n.tag.node))
            .map((n) => ({ key: n.tag.node._key, name: n.tag.node.name, value: Number(n.tag.value) || 0, label: label(n.tag.node) }))
            .toArray();
        const total = ADDITIVE_DIMENSIONS.includes(dimension)
            ? this.getEPMValueFormatter(dimension)(steps.reduce((t, s) => t + s.value, 0))
            : undefined;
        return createDimensionSummary(dimension, steps, HEATMAP_GRADIENT, scale, total);
    }

    // a step's card: what it is, who does it with what, and its figures; with the heatmap's value first while one is shown
    nodeToolTip = (item: INode): HTMLElement | undefined => {
        const node: AnalysisNode | undefined = item.tag?.node;
        if (!node || !this.organisation) return undefined;
        if (isOtherModel(node)) {
            return renderNodeTip({ kind: 'Model not shown', title: node.name, facts: [], figures: [],
                description: 'The models shown join it or use its information, or it theirs. Each connection names the information it carries.', footer: 'Double-click to open it' });
        }
        const names =new Map((this.organisationModelData?.nodes || []).map((n) => [n._key, n.name]));
        const tags = Object.values(node.tagKeys || {}).map((k) => findTagForOrganisation(this.organisation, k)).filter((t) => !!t).map((t) => ({ group: t.group.name, tag: t.tag.name }));
        const highlight = this.heatmapLegend && this.selectedDimension
            ? { label: String(this.selectedDimension), value: this.getEPMLabelForNode(this.selectedDimension)(node) || 'none' }
            : undefined;
        const tip = analysisNodeTip(node, {
            format: tipFormat(getCurrency(this.organisation.currencyCode || DEFAULT_CURRENCY)),
            modelName: this.organisation.models?.find((m) => m._key === node.modelKey)?.name,
            nameOf: (key) => names.get(key) || '', tags, highlight,
        });
        // the work off the main flow: the steps and information it stands for, with their figures added up
        if (isGrouped(node)) {
            const steps = node.members.filter((m) => m.superType === NODE_SUPERTYPE.ACTION).map((s) => s.name);
            const other = node.members.filter((m) => m.superType !== NODE_SUPERTYPE.ACTION).map((s) => s.name);
            const list = (names: string[]) => names.length <= 6 ? names.join(', ') : `${names.slice(0, 5).join(', ')} and ${names.length - 5} more`;
            tip.kind = 'Off the main flow';
            tip.facts = tip.facts.filter((f) => f.label !== 'Place');
            if (other.length) tip.facts.unshift({ label: 'Makes', value: list(other) });
            if (steps.length) tip.facts.unshift({ label: 'Steps', value: list(steps) });
            tip.footer = 'Show actions to see each step';
        }
        return renderNodeTip(tip);
    };

    // while a heatmap is shown: a step's name and value, or for a connection what it carries, between which steps, and
    // its share of the step's value
    heatmapToolTip = (item: INode | IEdge): string | undefined => {
        if (!this.heatmapLegend || !this.selectedDimension) return undefined;
        if (item instanceof INode) {
            const analysisNode: AnalysisNode | undefined = item.tag?.node;
            if (!analysisNode) return undefined;
            const value = this.getEPMLabelForNode(this.selectedDimension)(analysisNode) || 'none';
            return `${analysisNode.name}: ${value}`;
        }
        const from: AnalysisNode | undefined = item.sourceNode.tag?.node;
        const to: AnalysisNode | undefined = item.targetNode.tag?.node;
        if (!from || !to) return undefined;
        const value = this.getEPMValueFormatter(this.selectedDimension)(Number(item.tag?.value) || 0);
        const route = `${from.name} → ${to.name}`;
        const carries = item.tag?.edge?.informationName;
        return `${carries ? `${carries} (${route})` : route}: ${value}`;
    };

    private removeDimensionLabels() {
        const { graph } = this.graphComponent;
        graph.labels
            .filter((l) => l.tag?.type === LABEL_TYPES.COSTS)
            .toArray()
            .forEach((l) => graph.remove(l));
    }

    // a badge on each step with some of the dimension, coloured as the step glows; steps with none have no badge (hovering
    // still gives their value)
    private labelSteps(dimension: EPM_DIMENSION, scale: number) {
        const { graph } = this.graphComponent;
        const fnLabel = this.getEPMLabelForNode(dimension);
        graph.nodes.toArray().forEach((n) => {
            const value = Number(n.tag.value) || 0;
            const text = value > 0 ? fnLabel(n.tag.node) : '';
            this.setLabel(graph, n, text, scale > 0 ? value / scale : 0);
        });
    }

    // centred on the step's bottom edge, so it reads as part of the step without covering its name
    private static readonly BADGE_PLACEMENT = FreeNodeLabelModel.INSTANCE.createParameter([0.5, 1], [0, 0], [0.5, 0.5]);
    private static readonly BADGE_SHADES = 20;

    private badgeStyle(share: number): LabelStyle {
        const shade = Math.round(Math.min(1, Math.max(0, share)) * AnalysisLayoutComponent.BADGE_SHADES);
        let style = this.badgeStyles.get(shade);
        if (!style) {
            const c = channelsAt(HEATMAP_GRADIENT, shade / AnalysisLayoutComponent.BADGE_SHADES) || { r: 255, g: 255, b: 255, a: 255 };
            style = new LabelStyle({
                shape: 'pill',
                backgroundFill: `rgb(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)})`,
                // white keeps the badge apart from the glow and the step's outline
                backgroundStroke: '1.5px #ffffff',
                textFill: readableTextOn(c),
                font: '600 11px Inter, "Segoe UI", Arial, sans-serif',
                padding: [2, 8],
                horizontalTextAlignment: 'center',
                verticalTextAlignment: 'center',
            });
            this.badgeStyles.set(shade, style);
        }
        return style;
    }

    setLabel(graph: IGraph, node: INode, text: string, share: number) {
        const label = node.labels.find(
            (l) => l.tag && l.tag.type === LABEL_TYPES.COSTS,
        );
        if (!text) {
            if (label) graph.remove(label);
            return;
        }
        const style = this.badgeStyle(share);
        if (label) {
            graph.setLabelText(label, text);
            graph.setStyle(label, style);
        } else {
            graph.addLabel({
                owner: node,
                text,
                layoutParameter: AnalysisLayoutComponent.BADGE_PLACEMENT,
                style,
                tag: { type: LABEL_TYPES.COSTS },
            });
        }
    }

    // the outputs nothing uses, that carry no value or revenue, as the EPM scorecard counts dead ends: LINQ's count
    // of their consumers across every model, and the graph shown. Realising an outcome is only use of an output once a
    // value or revenue is set on it
    private deadEndOutputs(): Set<string> {
        const byKey = new Map(this.organisationData.nodes.map((n) => [n._key, n]));
        const used = new Set<string>();
        this.organisationData.edges.forEach((e) => {
            const from = byKey.get(e.from);
            if (!from || isUseOf(e, from)) used.add(e.from);
            if (e.informationKey && e.to !== e.informationKey) used.add(e.informationKey);
        });
        return new Set(this.organisationData.nodes
            .filter((n) => n.superType === NODE_SUPERTYPE.INFORMATION && !n.value && !n.revenue && !(n.consumers > 0) && !used.has(n._key))
            .map((n) => n._key));
    }

    // the members whose values a gathered node adds up: its steps, and for friction its dead ends too
    private countedSteps(node: GroupedNode, dimension: EPM_DIMENSION) {
        return dimension === EPM_DIMENSION.FRICTION ? node.members : node.members.filter((m) => m.superType === NODE_SUPERTYPE.ACTION);
    }

    // a node's value of the dimension: a step's own, or for a run of steps the sum of its steps'
    getEPMFunctionForNode(dimension: EPM_DIMENSION): (node: AnalysisNode) => number {
        const step = this.stepValueFunction(dimension);
        return (node: AnalysisNode) => isGrouped(node)
            ? this.countedSteps(node, dimension).reduce((t, m) => t + (step(m) || 0), 0)
            : step(node);
    }

    // a node's value of the dimension, in words: for a run of steps, the sum of its steps' and how many have any
    getEPMLabelForNode(dimension: EPM_DIMENSION): (node: AnalysisNode) => string {
        const step = this.stepLabelFunction(dimension);
        const stepValue = this.stepValueFunction(dimension);
        const format = this.getEPMValueFormatter(dimension);
        return (node: AnalysisNode) => {
            if (!isGrouped(node)) return step(node);
            const counted = this.countedSteps(node, dimension).filter((m) => (stepValue(m) || 0) > 0);
            const total = counted.reduce((t, m) => t + (stepValue(m) || 0), 0);
            return total > 0 ? `${format(total)} across ${counted.length} ${counted.length === 1 ? 'step' : 'steps'}` : '';
        };
    }

    private stepValueFunction(
        dimension: EPM_DIMENSION,
    ): (node: AnalysisNode) => number {
        switch (dimension) {
            case EPM_DIMENSION.CARBON:
                return (node: AnalysisNode) => node.carbon;
            case EPM_DIMENSION.FRICTION: {
                // friction as the EPM scorecard counts it: the cost of exception work (steps on a detour from the main
                // flow, or marked as such), of other work marked as not adding value, and of the dead ends (outputs
                // nothing uses, that carry no value or revenue)
                const deadEnds = this.deadEndOutputs();
                return (node: AnalysisNode) => (node.exceptionWork || isWaste(node) ? node.cost || 0 : deadEnds.has(node._key) ? node.totalCost || 0 : 0);
            }
            case EPM_DIMENSION.COST:
                return (node: AnalysisNode) => node.cost;
            case EPM_DIMENSION.OPTIONS:
                return (node: AnalysisNode) => 0;
            case EPM_DIMENSION.REVENUE:
                return (node: AnalysisNode) => node.revenue;
            case EPM_DIMENSION.REVENUE_CONTRIBUTION:
                return (node: AnalysisNode) => node.contributedRevenue || 0;
            case EPM_DIMENSION.COST_ABOVE_REVENUE:
                return (node: AnalysisNode) => costAboveRevenue(node);
            case EPM_DIMENSION.REVENUE_AT_RISK:
                return (node: AnalysisNode) => node.atRiskRevenue || 0;
            case EPM_DIMENSION.RISK:
                return (node: AnalysisNode) => 0;
            case EPM_DIMENSION.TIME:
                return (node: AnalysisNode) => node.hours;
            case EPM_DIMENSION.VALUE:
                return (node: AnalysisNode) => node.value;
            case EPM_DIMENSION.VALUE_CONTRIBUTION:
                return (node: AnalysisNode) => node.contributedValue || 0;
            case EPM_DIMENSION.DERIVED_VALUE:
                return (node: AnalysisNode) => node.derivedValue || 0;
            case EPM_DIMENSION.NO_VALUE:
                return (node: AnalysisNode) => (node.value === 0 ? 100 : 0);
            case EPM_DIMENSION.OUTPUTS_SUPPORTED:
                return (node: AnalysisNode) => node.outputsSupported;
            case EPM_DIMENSION.UTILISATION:
                // steps with no capacity modelled have no utilisation, rather than an infinite one
                return (node: AnalysisNode) =>
                    node.capacity ? Math.round((100 * node.hours) / node.capacity) : 0;
            case EPM_DIMENSION.THROUGHPUT:
                return (node: AnalysisNode) => 0;
            case EPM_DIMENSION.CENTRALITY:
                return (node: AnalysisNode) => node.centrality;
            default:
                return (node: AnalysisNode) => 0;
        }
    }

    // a value of the dimension with its units, for the legend's scale
    getEPMValueFormatter(dimension: EPM_DIMENSION): (value: number) => string {
        const abbreviated = (v: number) => this.formatter.formatAbbreviatedNumber(v);
        switch (dimension) {
            case EPM_DIMENSION.CARBON:
                return (v) => `${abbreviated(v)} kg CO2e`;
            case EPM_DIMENSION.FRICTION:
            case EPM_DIMENSION.COST:
            case EPM_DIMENSION.REVENUE:
            case EPM_DIMENSION.REVENUE_CONTRIBUTION:
            case EPM_DIMENSION.COST_ABOVE_REVENUE:
            case EPM_DIMENSION.REVENUE_AT_RISK:
                return (v) => this.formatter.formatAbbreviatedNumber(v, this.currency);
            case EPM_DIMENSION.TIME:
                return (v) => `${abbreviated(v)} ${v === 1 ? 'hour' : 'hours'}`;
            case EPM_DIMENSION.VALUE:
            case EPM_DIMENSION.VALUE_CONTRIBUTION:
                return (v) => `${abbreviated(v)} value`;
            case EPM_DIMENSION.DERIVED_VALUE:
                return (v) => `${Math.round(v)} of 100`;
            case EPM_DIMENSION.OUTPUTS_SUPPORTED:
                return (v) => `${abbreviated(v)} outputs`;
            case EPM_DIMENSION.NO_VALUE:
            case EPM_DIMENSION.UTILISATION:
                return (v) => `${Math.round(v)}%`;
            case EPM_DIMENSION.CENTRALITY:
                return (v) => v.toFixed(2);
            default:
                return abbreviated;
        }
    }

    private stepLabelFunction(
        dimension: EPM_DIMENSION,
    ): (node: AnalysisNode) => string {
        switch (dimension) {
            case EPM_DIMENSION.CARBON:
                return (node: AnalysisNode) =>
                    `${this.formatter.formatAbbreviatedNumber(node.carbon)} kg CO2e`;
            case EPM_DIMENSION.FRICTION: {
                // the cost of the exception work or dead end, and for a detour how much longer its route takes than the main one
                const deadEnds = this.deadEndOutputs();
                return (node: AnalysisNode) => {
                    const longer = node.friction ? ` (${node.friction.toFixed(1)}× the quickest route)` : '';
                    if (node.exceptionWork) return `${this.formatter.formatAbbreviatedNumber(node.cost || 0, this.currency)} exception work${longer}`;
                    if (isWaste(node)) return `${this.formatter.formatAbbreviatedNumber(node.cost || 0, this.currency)} not value adding`;
                    if (deadEnds.has(node._key)) return `${this.formatter.formatAbbreviatedNumber(node.totalCost || 0, this.currency)} dead end`;
                    return '';
                };
            }
            case EPM_DIMENSION.COST:
                return (node: AnalysisNode) =>
                    this.formatter.formatAbbreviatedNumber(
                        node.cost,
                        this.currency,
                    );
            case EPM_DIMENSION.OPTIONS:
                return (node: AnalysisNode) => '';
            case EPM_DIMENSION.REVENUE:
                return (node: AnalysisNode) =>
                    this.formatter.formatAbbreviatedNumber(
                        node.revenue,
                        this.currency,
                    );
            case EPM_DIMENSION.RISK:
                return (node: AnalysisNode) => '';
            case EPM_DIMENSION.TIME:
                return (node: AnalysisNode) =>
                    `${this.formatter.formatAbbreviatedNumber(node.hours)} ${node.hours === 1 ? 'hour' : 'hours'}`;
            case EPM_DIMENSION.VALUE:
                return (node: AnalysisNode) =>
                    `${this.formatter.formatAbbreviatedNumber(node.value)} value`;
            case EPM_DIMENSION.VALUE_CONTRIBUTION:
                return (node: AnalysisNode) => (node.contributedValue ? `${this.formatter.formatAbbreviatedNumber(node.contributedValue)} value` : '');
            case EPM_DIMENSION.REVENUE_CONTRIBUTION:
                return (node: AnalysisNode) => (node.contributedRevenue ? this.formatter.formatAbbreviatedNumber(node.contributedRevenue, this.currency) : '');
            case EPM_DIMENSION.REVENUE_AT_RISK:
                return (node: AnalysisNode) => (node.atRiskRevenue ? `${this.formatter.formatAbbreviatedNumber(node.atRiskRevenue, this.currency)} at risk` : '');
            case EPM_DIMENSION.COST_ABOVE_REVENUE:
                return (node: AnalysisNode) => (costAboveRevenue(node) > 0 ? `${this.formatter.formatAbbreviatedNumber(costAboveRevenue(node), this.currency)} above its revenue` : '');
            case EPM_DIMENSION.DERIVED_VALUE:
                return (node: AnalysisNode) => (node.derivedValue ? `${Math.round(node.derivedValue)} of 100 derived` : '');
            case EPM_DIMENSION.NO_VALUE:
                return (node: AnalysisNode) => (node.value === 0 ? 'No value' : '');
            case EPM_DIMENSION.OUTPUTS_SUPPORTED:
                return (node: AnalysisNode) =>
                    `${this.formatter.formatAbbreviatedNumber(node.outputsSupported)} outputs`;
            case EPM_DIMENSION.UTILISATION:
                return (node: AnalysisNode) =>
                    node.capacity ? `${Math.round((100 * node.hours) / node.capacity)}%` : '';
            case EPM_DIMENSION.CENTRALITY:
                return (node: AnalysisNode) => (node.centrality ?? 0).toFixed(2);
            default:
                return (node: AnalysisNode) => '';
        }
    }
}
